import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { ChevronUp } from 'lucide-react';

export default function LongPressMenu({ children, options, onSelect, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const longPressThreshold = 1000; // 1 second as requested

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate menu position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 240;
      const menuHeight = Math.min(options.length * 48 + 16, 320); // Approximate height
      
      // Calculate left position - try to center, but keep on screen
      let left = rect.left + (rect.width / 2) - (menuWidth / 2);
      if (left < 10) left = 10;
      if (left + menuWidth > window.innerWidth - 10) {
        left = window.innerWidth - menuWidth - 10;
      }
      
      // Calculate top position - prefer above the button
      let top = rect.top - menuHeight - 8;
      
      // If not enough space above, position below
      if (top < 10) {
        top = rect.bottom + 8;
      }
      
      // If still not enough space, just position at a reasonable location
      if (top + menuHeight > window.innerHeight - 10) {
        top = window.innerHeight - menuHeight - 10;
      }
      
      setMenuStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${menuWidth}px`,
        zIndex: 99999,
      });
    }
  }, [isOpen, options.length]);

  const handlePressStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    longPressTriggered.current = false;

    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      setIsOpen(true);
    }, longPressThreshold);
  };

  const handlePressEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    if (!isOpen && longPressTriggered.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleOptionClick = (e, option) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    onSelect(option);
  };

  const menu = isOpen ? createPortal(
    <div
      ref={menuRef}
      style={menuStyle}
      className="bg-white rounded-lg shadow-2xl border-2 border-slate-300 py-2 max-h-[320px] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
      data-testid="long-press-menu"
    >
      {options.map((option, index) => (
        <button
          key={index}
          onMouseDown={(e) => handleOptionClick(e, option)}
          onClick={(e) => handleOptionClick(e, option)}
          className="w-full px-4 py-3 text-left hover:bg-dp-100 active:bg-dp-200 transition-colors text-sm font-medium text-slate-800 flex items-center gap-3 cursor-pointer"
          data-testid={`menu-option-${option.value}`}
          type="button"
        >
          {option.icon && <span className="text-lg">{option.icon}</span>}
          <span>{option.label}</span>
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <Button
        ref={buttonRef}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        className={`${className} flex items-center justify-center gap-2`}
        type="button"
        data-testid="long-press-button"
      >
        {children}
        <ChevronUp className="w-4 h-4" />
      </Button>
      {menu}
    </>
  );
}
